import { CONTENT_LIMITS, type CourseDocument } from '@eklevo/shared/course'
import { LOCALES, type Locale } from '@eklevo/shared/i18n/editor'
import { useRef, useState, type RefObject } from 'react'
import { errorIssues, errorReason, sessionGone } from '../../lib/errors'
import { Field } from '../ui/field'
import { FormDialog } from '../ui/form-dialog'
import { Select } from '../ui/select'
import { TextArea } from '../ui/text-area'
import { TextInput } from '../ui/text-input'
import { EditorI18nProvider, useEditorI18n } from './i18n'

/*
 * «Crear nuevo curso» (PRD 03 §2.2 and PRD 04 §2.5; Figma 91:3488 and 91:3162): the course's
 * name, its description (500 characters, with the count), its language and, if it is one level
 * of a series, the series and the level (ADR 0006), and «Crear y empezar a editar», which opens
 * the editor with the course empty. Without the Figma's «Número de niveles» and its help text:
 * one course is created, and its place in a series is given here or in the editor (ADR 0009 §4).
 * The server checks the fields (packages/core, `validateNewCourse`); its problems show at their
 * fields. Nothing is created until it is sent, so closing it leaves no course behind. Shared by
 * HR's console and the back office, each with its own call.
 */

/** What the dialog sends: the server makes the course's draft with it. */
export type NewCourseFields = Pick<
  CourseDocument,
  'title' | 'description' | 'language' | 'series' | 'level'
>

const L = CONTENT_LIMITS
type Problems = Partial<Record<keyof NewCourseFields, string>>
const FIELDS = new Set<string>(['title', 'description', 'language', 'series', 'level'])

export function CreateCourseDialog({
  locale,
  ...props
}: {
  /** The interface's language, the surface's; the course's own starts as this one. */
  locale: Locale
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Creates the course on the server; it fails as the surface's API client fails. Through the
   * surface's query client, which ends a session the server no longer knows.
   */
  create: (fields: NewCourseFields) => Promise<{ courseId: string }>
  /** The course was created: the surface opens its editor. */
  onCreated: (courseId: string) => void
  /** The button that opened it, where the focus goes back once it closes. */
  opener: RefObject<HTMLElement | null>
}) {
  // A new form each time it opens: nothing typed before stays. It stays while it closes, for
  // the focus to go back where it came from.
  const [round, setRound] = useState(0)
  const [wasOpen, setWasOpen] = useState(props.open)
  if (props.open !== wasOpen) {
    setWasOpen(props.open)
    if (props.open) setRound((r) => r + 1)
  }
  return (
    <EditorI18nProvider locale={locale}>
      <CreateCourseForm key={round} locale={locale} {...props} />
    </EditorI18nProvider>
  )
}

function CreateCourseForm({
  locale,
  open,
  onOpenChange,
  create,
  onCreated,
  opener,
}: {
  locale: Locale
  open: boolean
  onOpenChange: (open: boolean) => void
  create: (fields: NewCourseFields) => Promise<{ courseId: string }>
  onCreated: (courseId: string) => void
  opener: RefObject<HTMLElement | null>
}) {
  const { t } = useEditorI18n()
  const first = useRef<HTMLInputElement>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [language, setLanguage] = useState<Locale>(locale)
  const [series, setSeries] = useState('')
  const [level, setLevel] = useState('')
  const [problems, setProblems] = useState<Problems>({})
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    setError(null)
    // What is missing is said at once; everything else is the server's to check.
    const missing: Problems = {}
    if (!title.trim()) missing.title = t('problem.required')
    if (!description.trim()) missing.description = t('problem.required')
    setProblems(missing)
    if (Object.keys(missing).length > 0) return focusFirst(missing)
    setBusy(true)
    try {
      const { courseId } = await create({
        title,
        description,
        language,
        series: series.trim() === '' ? null : series.trim(),
        level: level === '' ? null : Number(level),
      })
      onCreated(courseId)
    } catch (err) {
      // The surface ends a session the server no longer knows (its query client's cache).
      if (sessionGone(err)) return
      const found: Problems = {}
      for (const issue of errorIssues(err)) {
        const name = issue.path[0]
        if (typeof name === 'string' && FIELDS.has(name) && !found[name as keyof Problems])
          found[name as keyof Problems] = t(`problem.${issue.code}` as Parameters<typeof t>[0])
      }
      if (errorReason(err) === 'invalid' && Object.keys(found).length > 0) {
        setProblems(found)
        focusFirst(found)
      } else
        setError(t(errorReason(err) === 'too-many-courses' ? 'create.error.limit' : 'create.error'))
    } finally {
      setBusy(false)
    }
  }
  /** The first field with a problem takes the focus, in the form's order. */
  const focusFirst = (found: Problems) => {
    const name = ['title', 'description', 'language', 'series', 'level'].find(
      (n) => found[n as keyof Problems],
    )
    if (name) document.querySelector<HTMLElement>(`[data-create="${name}"]`)?.focus()
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('create.title')}
      description={t('create.body')}
      submitLabel={t('create.submit')}
      busyLabel={t('create.busy')}
      cancelLabel={t('create.cancel')}
      busy={busy}
      error={error}
      onSubmit={() => void submit()}
      initialFocus={first}
      opener={opener}
    >
      <Field label={t('create.name')} required error={problems.title}>
        <TextInput
          ref={first}
          data-create="title"
          value={title}
          maxLength={L.course.title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field>
      <Field
        label={t('create.description')}
        required
        hint={t('general.description.count', {
          count: description.length,
          max: L.course.description,
        })}
        error={problems.description}
      >
        <TextArea
          data-create="description"
          rows={3}
          value={description}
          maxLength={L.course.description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <Field label={t('general.language')} required error={problems.language}>
        <Select
          data-create="language"
          value={language}
          onChange={(e) => setLanguage(e.target.value as Locale)}
        >
          {LOCALES.map((l) => (
            <option key={l} value={l}>
              {t(`language.${l}`)}
            </option>
          ))}
        </Select>
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('general.series')} hint={t('create.series.hint')} error={problems.series}>
          <TextInput
            data-create="series"
            value={series}
            maxLength={L.slug}
            autoCapitalize="none"
            spellCheck={false}
            onChange={(e) => setSeries(e.target.value)}
          />
        </Field>
        <Field label={t('general.level')} hint={t('general.level.hint')} error={problems.level}>
          <Select data-create="level" value={level} onChange={(e) => setLevel(e.target.value)}>
            <option value="">{t('general.level.none')}</option>
            {Array.from({ length: L.course.levels }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {t('general.level.option', { level: n })}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </FormDialog>
  )
}
